// src/AnswerMessage.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  makeStyles,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { Bubble } from "../ds";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from "remark-supersub";
import { FollowUpChips } from "./FollowUpChips";
import { CitationPills } from "./CitationPills";
import { AnswerActions } from "./AnswerActions";
import { Icon } from '@brand/Logo';
import { getBrandConfig } from '../brands/brand-registry';
import type { ResponseLanguage } from '../api/models';

// Constants for better maintainability
const AVATAR_SIZES = {
  desktop: { width: 32, height: 32 },
  tablet: { width: 24, height: 24 },
  mobile: { width: 20, height: 20 }
} as const;

// Brand Avatar component with responsive sizing (decorative for accessibility)
const BrandAvatar: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <Icon 
      width={AVATAR_SIZES.desktop.width} 
      height={AVATAR_SIZES.desktop.height} 
      className={className}
      decorative={true}
    />
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalS,
    maxWidth: "70%", // Desktop: 70% width constraint
    width: "100%",
    '@media (max-width: 600px)': {
      maxWidth: "95%", // Tablet: more space
      marginTop: tokens.spacingVerticalXS,
      marginBottom: tokens.spacingVerticalXS,
      paddingLeft: tokens.spacingHorizontalXXS,
      paddingRight: tokens.spacingHorizontalXXS,
    },
  },
  avatar: {
    float: "left", // Float left so text flows around it
    marginRight: tokens.spacingHorizontalXS, // Minimal gap between avatar and text
    marginBottom: tokens.spacingVerticalXXS, // Minimal space below avatar
    width: `${AVATAR_SIZES.desktop.width}px`,
    height: `${AVATAR_SIZES.desktop.height}px`,
    '@media (max-width: 600px)': {
      width: `${AVATAR_SIZES.mobile.width}px`,
      height: `${AVATAR_SIZES.mobile.height}px`,
      marginRight: tokens.spacingHorizontalXXS,
    }
  },
  errorMessage: {
    display: "flex",
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
  },
  cardWithAvatar: {
    position: "relative",
    overflow: "hidden", // Ensure floated content doesn't overflow
    textAlign: "left", // Ensure text alignment
    display: "block", // Block display for proper text flow
    width: "100%", // Full width for text flow
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    '@media (max-width: 600px)': {
      paddingTop: tokens.spacingVerticalS,
      paddingBottom: tokens.spacingVerticalS,
      paddingLeft: tokens.spacingHorizontalS,
      paddingRight: tokens.spacingHorizontalS
    }
  },
  content: {
    display: "block", // Change from flex to block for proper text flow
    width: "100%", // Ensure full width for text flow
  },
  content_response: {
    width: "100%", // Full width for text to flow around avatar
    display: "block", // Block display for proper text flow
  },
  skeleton: {
    width: "100%",
    marginTop: tokens.spacingVerticalS,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "60px",
  },
  initialLoader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  eventDisplay: {
    textAlign: "center",
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingVerticalM,
    paddingRight: tokens.spacingVerticalM,
  },
  eventMessage: {
    marginTop: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontStyle: "italic",
  },
  anchor_tag: {
    margin: 0,
    padding: 0,
    color: tokens.colorBrandForegroundLink,
    textDecoration: "underline",
    fontSize: "var(--message-text-size, 15px)",
    "&:hover": {
      textDecoration: "underline",
      color: tokens.colorBrandForegroundLinkHover,
    },
  },
});

const markdownComponents = {
  a: ({ node, ...props }: any) => (
    <a
      className={useStyles().anchor_tag}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {props.children}
    </a>
  ),
};

interface Props {
  englishText: string; // Contains response in target language (not just English)
  citations: string[];
  citationTitles?: Record<string, string>; // URL -> Title mapping
  followUpQuestions: string[]; // Contains followups in target language
  eventMessages: string[];
  error?: string;
  domId: string;
  conversationId: string;
  messageId: string;
  onFollowUpClick: (question: string) => void;
  isThinking?: boolean;
  isStreaming?: boolean;
  prefetchedAudio?: string;
  prefetchedVoice?: string;
  prefetchedSpeed?: number;
  voiceSynthesisStatus?: "idle" | "pending" | "ready" | "error";
  responseLanguage?: ResponseLanguage;
  isLatestAssistant?: boolean;
  precedingQuestion?: string; // User's question for export context
  onRateLimitError?: (error: any) => void;
}

export const AnswerMessage: React.FC<Props> = ({
  englishText,
  citations,
  citationTitles = {},
  followUpQuestions,
  eventMessages,
  error,
  domId,
  conversationId,
  messageId,
  onFollowUpClick,
  isThinking = false,
  isStreaming = false,
  prefetchedAudio,
  prefetchedVoice,
  prefetchedSpeed,
  voiceSynthesisStatus,
  responseLanguage,
  isLatestAssistant = false,
  precedingQuestion = '',
  onRateLimitError,
}) => {
  const styles = useStyles();
  const brandConfig = getBrandConfig();
  const [displayedText, setDisplayedText] = useState<string>("");
  const bubbleRef = useRef<HTMLDivElement>(null);
  const wasCompleteRef = useRef<boolean>(false);
  
  useEffect(() => {
    setDisplayedText(englishText); // englishText now contains response in target language
  }, [englishText]);
  
  // Only show citations, actions, and follow-ups when answer is complete
  const isAnswerComplete = !isThinking && !isStreaming && englishText && englishText.trim().length > 0;
  
  // Auto-focus the answer card when it becomes complete (only for the latest assistant message)
  useEffect(() => {
    if (isAnswerComplete && isLatestAssistant && !wasCompleteRef.current && bubbleRef.current) {
      // Small delay to ensure DOM is fully updated
      const timer = setTimeout(() => {
        bubbleRef.current?.focus();
      }, 100);
      wasCompleteRef.current = true;
      return () => clearTimeout(timer);
    }
    // Reset when answer becomes incomplete (e.g., new question asked)
    if (!isAnswerComplete) {
      wasCompleteRef.current = false;
    }
  }, [isAnswerComplete, isLatestAssistant]);

  const skeletonControl = (
    <div className={styles.skeleton}>
      {eventMessages.length > 0 ? (
        <div className={styles.eventDisplay}>
          <strong>{brandConfig.messages.processing}</strong>
          <div className={styles.eventMessage}>
            {eventMessages[eventMessages.length - 1]}
          </div>
        </div>
      ) : (
        <div className={styles.initialLoader}>
          <Spinner
            labelPosition="after"
            label={brandConfig.messages.initializing}
            size="small"
          />
        </div>
      )}
    </div>
  );

  // Generate unique ID for the content area
  const contentId = `answer-content-${domId}`;
  const labelId = `answer-label-${domId}`;

  const responseContentControl = (
    <div className={styles.content}>
      {/* Hidden label for screen reader context */}
      <span id={labelId} className="sr-only">
        {brandConfig.messages.botResponseLabel || 'Answer from AskCI'}: 
      </span>
      <div 
        id={contentId}
        className={styles.content_response}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm, supersub]}
          components={markdownComponents}
        >
          {displayedText}
        </ReactMarkdown>
      </div>
    </div>
  );

  const renderContent = isThinking ? skeletonControl : responseContentControl;

  return (
    <div className={styles.container}>
      <Bubble 
        kind="bot" 
        className={styles.cardWithAvatar}
        aria-labelledby={isAnswerComplete ? `${labelId} ${contentId}` : undefined}
        ref={bubbleRef}
      >
        <BrandAvatar className={styles.avatar} />
        {error ? (
          <div className={styles.errorMessage}>{error}</div>
        ) : (
          renderContent
        )}

                {/* Citation pills and actions - only show when answer is complete */}
                {isAnswerComplete && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: tokens.spacingHorizontalXS,
                    flexWrap: 'nowrap', // Keep on same line
                    minHeight: '32px', // Ensure consistent height
                  }}>
                    {/* Citations - inline with actions */}
                    <div style={{
                      width: '80%'
                    }}>
                    <CitationPills citations={citations} citationTitles={citationTitles} />
                    </div>
                    
                    {/* Actions - inline with citations */}
                    <AnswerActions
                      id={conversationId}
                      messageId={messageId}
                      question={precedingQuestion}
                      content={englishText}
                      citations={citations}
                      eventMessages={eventMessages}
                      prefetchedAudio={prefetchedAudio}
                      prefetchedVoice={prefetchedVoice}
                      prefetchedSpeed={prefetchedSpeed}
                      voiceSynthesisStatus={voiceSynthesisStatus}
                      responseLanguage={responseLanguage}
                      onRateLimitError={onRateLimitError}
                    />
                  </div>
                )}
              </Bubble>

              {/* Floating chips outside the card - only show when answer is complete */}
              {isAnswerComplete && followUpQuestions.length > 0 && (
                <FollowUpChips
                  questions={followUpQuestions} // Already in target language
                  onFollowUpClick={onFollowUpClick}
                />
              )}
    </div>
  );
};
